import { trackEvent } from '@patrianna/analytics'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import { CheckCircleOutline } from '@patrianna-icons/material-icons'
import useKYCIdVerificationStep from '@patrianna-payments/shared-store/verifications/hooks/docs-verification/useKYCIdVerificationStep'
import { setKYCInfoRequest } from '@patrianna-payments/shared-store/verifications/store/actions'
import cx from 'classnames'

import { stepOneBtnText } from 'dialogs/RedeemFlow/SubmitDocuments/utils'
import { useReturnAfterVerification } from 'hooks/useReturnAfterVerification'
import content from 'prismic-translates/dialog-identity-verification.json'
import Button from 'src/components/Button'
import { serviceDependencies } from 'src/services'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { getKycStatusSelector } from 'store/modules/kyc/selectors'
import { getUserIdSelector } from 'store/modules/user/selectors'

import classes from './styles.module.scss'
import SuccessfullyVerified from './SuccessfullyVerified'
import VerificationInProgress from './VerificationInProgress'

import type { VerificationFromProps } from 'hooks/useReturnAfterVerification'

const t = getTranslationFromJson([content])

type Props = {
  isRedeeming: boolean
  verificationFrom: VerificationFromProps
}

function IdVerificationStep({ isRedeeming, verificationFrom }: Props) {
  const dispatch = useAppDispatch()
  const kycStatus = useAppSelector(getKycStatusSelector)
  const userId = useAppSelector(getUserIdSelector)

  const { error } = useKYCIdVerificationStep({ fraud: serviceDependencies.fraud }, kycStatus)
  const { handleSetVerificationFromToParams } = useReturnAfterVerification()

  const confirmedStatus = kycStatus === 'confirmed'
  const secondStep = ['id_confirmed', 'doc_declined', 'doc_review'].includes(kycStatus)
  const inReview = kycStatus === 'in_review'

  const firstStepBtnText = stepOneBtnText(kycStatus)

  const submitId = () => {
    handleSetVerificationFromToParams(verificationFrom)
    void trackEvent('KYC_ID_Upload_Initiated', { category: userId })
    dispatch(setKYCInfoRequest({ phase: 'identity_verification', error }))
  }

  if (secondStep || confirmedStatus) {
    return (
      <SuccessfullyVerified
        dataTest='verifications-verified-id'
        title={t('step1_id_verification')}
        text={t('id_verified')}
      />
    )
  }

  if (inReview) {
    return <VerificationInProgress title={t('step1_id_verification')} text={t('id_verification_in_progress')} />
  }

  return (
    <div className={classes.root}>
      {/* step 1 */}
      <h4 className={classes.title}>
        <b>{t('step1_id_verification')}</b>
      </h4>
      {error && (
        <p className={cx('text-smaller', classes.error)}>
          <img className={classes.iconProcess} src={`${process.env.IMG_ORIGIN}/icons/error.svg`} alt='error-icon' />
          {error}
        </p>
      )}
      <p className={classes.text}>{t('need_to_upload_photo')}</p>

      <Button
        data-test={`verifications-${firstStepBtnText}`}
        disabled={isRedeeming || confirmedStatus || inReview}
        onClick={submitId}
        className={classes.button}
      >
        {firstStepBtnText === 'verified' && <CheckCircleOutline className={classes.verifiedIcon} />}
        {firstStepBtnText}
      </Button>
    </div>
  )
}

export default IdVerificationStep
